/**
 * @fileoverview The shared sign-in shell — brand mark, portal tab strip, title,
 * and whichever form the route selected.
 *
 * Mounted as a LAYOUT route over /login, /student/login and /org/login, which
 * is what makes the tab switch feel like one screen: the shell stays mounted
 * across the three child routes, so the highlight pill can slide (a shared
 * framer `layoutId`) instead of the whole page tearing down and rebuilding.
 *
 * Two consequences of keeping the URLs:
 *   - every existing entry point still works untouched — the three 401
 *     interceptors (api.js → /login, studentApi.js → /student/login,
 *     orgApi.js → /org/login) and every emailed link land on the right tab.
 *   - a tab is a real <Link>, not a button with tab semantics. It navigates, so
 *     links are the honest element: keyboard, middle-click and screen readers
 *     all get the right behaviour for free. `replace` keeps Back pointed at
 *     wherever the visitor came from rather than cycling through tabs.
 */

import { Link, Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { AUTH_PORTALS, offeredPortals, portalForPath } from "@/lib/authPortals";

const EASE = [0.16, 1, 0.3, 1];

const AuthPortalLayout = () => {
  const { pathname } = useLocation();
  const active = portalForPath(pathname) || AUTH_PORTALS[0];
  const tabs = offeredPortals(active);

  // Anchored to the TOP rather than vertically centred, and that is the whole
  // reason the tab strip holds still. The three forms are different heights
  // (the instructor tab carries Google sign-in; its sign-up mode is taller
  // again), so a centred card re-centres on every switch — the brand mark and
  // the tabs would slide ~56px while the highlight moves, which reads as the
  // page reloading. Top-anchored, only the form below the tabs changes.
  return (
    <div className="flex min-h-screen items-start justify-center px-4 py-10 md:py-14 studio-spotlight">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="w-full max-w-md"
      >
        <Link to="/" className="mb-6 flex items-center justify-center gap-3">
          <img src="/logo192.png" alt="Hans" className="h-10 rounded-lg" />
          <span
            className="text-lg font-semibold tracking-tight text-[var(--text-primary)]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Hans
          </span>
        </Link>

        {/* Portal tabs */}
        <nav
          aria-label="Choose your portal"
          data-testid="auth-portal-tabs"
          className="mb-7 flex gap-1 rounded-full border border-[var(--studio-divider)]
                     bg-[var(--studio-raised)] p-1"
        >
          {tabs.map((portal) => {
            const selected = portal.id === active.id;
            const PortalIcon = portal.icon;
            return (
              <Link
                key={portal.id}
                to={portal.loginPath}
                replace
                aria-current={selected ? "page" : undefined}
                data-testid={`auth-tab-${portal.id}`}
                className="relative flex-1 rounded-full px-2 py-2 text-center outline-hidden
                           focus-visible:ring-1 focus-visible:ring-[var(--accent-yellow)]"
              >
                {selected && (
                  <motion.span
                    layoutId="auth-portal-tab"
                    className="absolute inset-0 rounded-full bg-[var(--studio-interactive)]
                               shadow-sm"
                    transition={{ type: "spring", stiffness: 420, damping: 36 }}
                  />
                )}
                <span
                  className={`relative flex items-center justify-center gap-1.5 text-xs font-medium
                              transition-colors duration-200 ${
                                selected
                                  ? "text-[var(--text-primary)]"
                                  : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
                              }`}
                >
                  <PortalIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
                  {portal.tab}
                </span>
              </Link>
            );
          })}
        </nav>

        {/* Title + form. Keyed on the portal so switching tabs fades the new
            copy in; no AnimatePresence, because the Outlet already renders the
            NEW route's form the moment the URL changes — a wait-mode exit would
            be animating the incoming form out. */}
        <motion.div
          key={active.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24, ease: EASE }}
        >
          <div className="mb-6 text-center">
            <h1
              className="mb-2 text-2xl font-light text-[var(--text-primary)]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {active.title}
            </h1>
            <p className="text-sm text-[var(--text-secondary)]">{active.subtitle}</p>
          </div>

          <Outlet />
        </motion.div>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-xs text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)]"
          >
            Back to home
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPortalLayout;
